<template>
  <div ref="group" class="salon-pin" role="group" :aria-labelledby="labelledby">
    <input
      v-for="(digit, index) in digits"
      :key="index"
      class="salon-pin-box"
      :class="{ 'salon-pin-box--filled': digit !== '' }"
      type="password"
      inputmode="numeric"
      autocomplete="one-time-code"
      maxlength="1"
      :name="`${name}_${index + 1}`"
      :aria-label="$t('salon.pin.digitLabel', { label, index: index + 1, length })"
      :value="digit"
      @input="onInput(index, $event)"
      @keydown="onKeydown(index, $event)"
      @focus="$event.target.select()"
      @paste="onPaste"
    />
  </div>
</template>

<script>
/**
 * The salon flow's boxed PIN entry — one box per digit, masked, auto-advancing.
 *
 * Shared by SH002 (sign in) and SH003 (choose a PIN) rather than written twice: the
 * focus handling below is easy to get subtly wrong on this repo's Vue, and a second copy
 * would be a second chance to reintroduce the bug described in focusAt().
 */
export default {
  name: "SalonPinInput",
  props: {
    // v-model: the PIN as a plain string, "" through "1234".
    modelValue: {
      type: String,
      default: "",
    },
    length: {
      type: Number,
      default: 4,
    },
    name: {
      type: String,
      default: "salon_pin",
    },
    label: {
      type: String,
      default: "PIN",
    },
    labelledby: {
      type: String,
      default: null,
    },
  },
  emits: ["update:modelValue"],
  computed: {
    digits() {
      const value = String(this.modelValue || "");
      return Array.from({ length: this.length }, (unused, index) => value[index] || "");
    },
  },
  methods: {
    emitDigits(digits) {
      // Trailing gaps are dropped so the parent can just check length === this.length.
      this.$emit("update:modelValue", digits.join("").slice(0, this.length));
    },

    onInput(index, event) {
      const typed = event.target.value.replace(/\D/g, "");
      // Typing into a filled box replaces it; the browser hands back both characters.
      const digit = typed.slice(-1);
      const digits = this.digits.slice();

      digits[index] = digit;
      event.target.value = digit;
      this.emitDigits(digits);

      if (digit && index < this.length - 1) {
        this.focusAt(index + 1);
      }
    },

    onKeydown(index, event) {
      if (event.key === "Backspace" && !this.digits[index] && index > 0) {
        // Nothing to delete here — step back and clear the box that does hold a digit.
        event.preventDefault();
        const digits = this.digits.slice();
        digits[index - 1] = "";
        this.emitDigits(digits);
        this.focusAt(index - 1);
        return;
      }

      if (event.key === "ArrowLeft" && index > 0) {
        event.preventDefault();
        this.focusAt(index - 1);
        return;
      }

      if (event.key === "ArrowRight" && index < this.length - 1) {
        event.preventDefault();
        this.focusAt(index + 1);
      }
    },

    // Pasting a whole PIN would otherwise land entirely in one box.
    onPaste(event) {
      event.preventDefault();
      const pasted = (event.clipboardData || window.clipboardData)
        .getData("text")
        .replace(/\D/g, "")
        .slice(0, this.length);

      if (!pasted) {
        return;
      }

      const digits = Array.from({ length: this.length }, (unused, i) => pasted[i] || "");
      this.emitDigits(digits);
      this.$nextTick(() => this.focusAt(Math.min(pasted.length, this.length - 1)));
    },

    // Read off the group element rather than a `ref` on the v-for: array refs inside
    // v-for only landed in Vue 3.2.25 and this app is on 3.2.0, where a v-for ref is
    // undefined — auto-advance silently did nothing and only one digit registered.
    focusAt(index) {
      const group = this.$refs.group;

      if (!group) {
        return;
      }

      const input = group.querySelectorAll(".salon-pin-box")[index];

      if (input) {
        input.focus();
        input.select();
      }
    },

    focusFirst() {
      this.focusAt(0);
    },
  },
};
</script>

<style scoped>
/* Tokens come from src/salon/styles/tokens.css, namespaced under .salon-app. */
.salon-pin {
  display: flex;
  gap: 12px;
}

.salon-pin-box {
  width: 56px;
  height: 64px;
  padding: 0;
  font-family: var(--salon-font-body);
  font-size: 24px;
  line-height: 32px;
  color: var(--salon-on-surface);
  text-align: center;
  background: var(--salon-surface-lowest);
  border: 1px solid var(--salon-border);
  border-radius: var(--salon-radius-input);
  outline: none;
  appearance: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.salon-pin-box--filled {
  border-color: var(--salon-primary-container);
}

.salon-pin-box:focus {
  border-color: var(--salon-primary-container);
  box-shadow: 0 0 0 1px var(--salon-primary-container);
}
</style>
